import { isOnline } from "./online";
import { type LiveEvent, type LiveStatus, STREAM_KINDS } from "./types";

/**
 * The EventSource of the leader tab, with the failure handling the browser does not do:
 *
 *   - The browser retries dropped connections by itself (Last-Event-ID resumes where it stopped).
 *   - When the server refuses (HTTP error), EventSource gives up with readyState CLOSED and no
 *     status. A probe request tells the cases apart: 410 means the history is gone (full reload),
 *     401/403 means the session ended (stop), anything else is retried with backoff.
 */

export interface StreamHandlers {
  /** Last seq this browser has applied; the next connection resumes after it. */
  since(): number;
  onEvent(ev: LiveEvent): void;
  onStatus(status: LiveStatus): void;
  /** The server cannot replay from `since`: reload everything, then call `open` again. */
  onGone(): void;
  onUnauthorized(): void;
}

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];

export class Stream {
  private es: EventSource | null = null;
  private retry: number | undefined;
  private attempt = 0;
  private probing: AbortController | null = null;
  private closed = true;

  constructor(private readonly h: StreamHandlers) {}

  get active() {
    return !this.closed;
  }

  open() {
    this.closed = false;
    this.connect(true);
  }

  close() {
    this.closed = true;
    this.teardown();
    window.clearTimeout(this.retry);
    this.retry = undefined;
  }

  /** The network is back: do not wait for the browser's own retry timer. */
  reconnectNow() {
    if (this.closed) return;
    window.clearTimeout(this.retry);
    this.retry = undefined;
    this.attempt = 0;
    this.connect(false);
  }

  private url() {
    return `/api/events?since=${this.h.since()}`;
  }

  private teardown() {
    this.probing?.abort();
    this.probing = null;
    if (this.es) {
      this.es.onopen = null;
      this.es.onerror = null;
      this.es.close();
      this.es = null;
    }
  }

  private connect(initial: boolean) {
    this.teardown();
    this.h.onStatus(initial ? "connecting" : "reconnecting");
    const es = new EventSource(this.url(), { withCredentials: true });
    this.es = es;
    es.onopen = () => {
      this.attempt = 0;
      this.h.onStatus("live");
    };
    for (const kind of STREAM_KINDS) {
      es.addEventListener(kind, (e) => {
        try {
          this.h.onEvent(JSON.parse((e as MessageEvent<string>).data) as LiveEvent);
        } catch (err) {
          console.error("[live] bad event", err);
        }
      });
    }
    es.onerror = () => {
      if (this.closed || this.es !== es) return;
      this.h.onStatus("reconnecting");
      // Still CONNECTING: the browser retries on its own. CLOSED: it gave up.
      if (es.readyState === EventSource.CLOSED) void this.probe();
    };
  }

  private async probe() {
    this.teardown();
    const ctl = new AbortController();
    this.probing = ctl;
    let status = 0;
    try {
      const res = await fetch(this.url(), {
        credentials: "same-origin",
        headers: { Accept: "text/event-stream" },
        signal: ctl.signal,
      });
      status = res.status;
      ctl.abort(); // only the status line was wanted
    } catch {
      if (ctl.signal.aborted && status === 0) return; // closed meanwhile
    }
    if (this.closed) return;
    if (status === 410) {
      this.h.onGone();
      return;
    }
    if (status === 401 || status === 403) {
      this.h.onUnauthorized();
      return;
    }
    this.scheduleRetry();
  }

  private scheduleRetry() {
    const wait = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)] as number;
    this.attempt++;
    this.h.onStatus(isOnline() ? "reconnecting" : "offline");
    this.retry = window.setTimeout(() => {
      this.retry = undefined;
      if (!this.closed) this.connect(false);
    }, wait);
  }
}
